import { useState, type KeyboardEvent } from 'react';
import { Send } from 'lucide-react';
import { useT } from '@/lib/i18n/useT';

interface ChatComposerProps {
  onSend: (text: string) => void;
  disabled?: boolean;
}

export function ChatComposer({ onSend, disabled }: ChatComposerProps) {
  const t = useT();
  const [value, setValue] = useState('');

  const submit = () => {
    const text = value.trim();
    if (!text || disabled) return;
    onSend(text);
    setValue('');
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  return (
    <div className="flex items-end gap-2 border-t border-[var(--color-xk-border)] bg-[var(--color-xk-bg-nav)] p-2">
      <textarea
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={onKeyDown}
        placeholder={t('assistant.placeholder')}
        rows={2}
        className="min-h-[44px] flex-1 resize-none rounded-md border border-[var(--color-xk-border)] bg-[var(--color-xk-bg)] px-2 py-1.5 text-[13px] text-[var(--color-xk-lavender)] placeholder:text-[var(--color-xk-text-subtle)] focus:border-[var(--color-xk-accent)] focus:outline-none"
        disabled={disabled}
      />
      <button
        type="button"
        onClick={submit}
        disabled={disabled || !value.trim()}
        aria-label={t('assistant.send')}
        className="grid h-9 w-9 flex-none place-items-center rounded-md bg-[var(--color-xk-accent)] text-black transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
      >
        <Send className="h-4 w-4" />
      </button>
    </div>
  );
}
